import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicBlogAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft,
  Calendar,
  User,
  Share2,
  Loader2,
  FileText,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { fbEvents } from '@/lib/facebook-pixel';

const PublicBlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentArticles, setRecentArticles] = useState([]);

  useEffect(() => {
    fetchArticle();
    fetchRecentArticles();
  }, [slug]);

  // Track ViewContent when article is loaded
  useEffect(() => {
    if (article) {
      fbEvents.viewBlogPost(article);
    }
  }, [article]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await publicBlogAPI.getArticleBySlug(slug);
      setArticle(response.data);
    } catch (error) {
      console.error('Erreur chargement article:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentArticles = async () => {
    try {
      const response = await publicBlogAPI.getRecentArticles(4);
      setRecentArticles(response.data.articles.filter(a => a.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Erreur chargement articles récents:', error);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt || article.meta_description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié dans le presse-papier !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <FileText className="w-16 h-16 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-700 mb-2">Article non trouvé</h1>
        <p className="text-slate-500 mb-6">Cet article n'existe pas ou a été supprimé.</p>
        <Link to="/portail/blog">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero / Header */}
      <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/portail/blog">
            <Button variant="ghost" className="text-white/80 hover:text-white mb-6 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
          </Link>
          
          {/* Keywords */}
          {article.keywords && article.keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {article.keywords.slice(0, 4).map((kw, idx) => (
                <Badge key={idx} className="bg-orange-500/20 text-orange-300 border-orange-500/30">
                  {kw}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            {article.title}
          </h1>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(article.published_at)}
            </div>
            {article.created_by_name && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {article.created_by_name}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-white/80 hover:text-white"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Partager
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-xl shadow-sm p-6 md:p-10">
            {/* Featured Image */}
            {article.featured_image && (
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
              />
            )}
            
            {/* Article Content */}
            <article
              className="prose prose-slate prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-slate-800
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:border-orange-200 prose-h2:pb-2
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-slate-600 prose-p:leading-relaxed
                prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-slate-800
                prose-ul:my-4 prose-li:text-slate-600
                prose-img:rounded-lg"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* Tags */}
            {article.keywords && article.keywords.length > 0 && (
              <div className="mt-10 pt-6 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">Mots-clés</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {article.keywords.map((kw, idx) => (
                    <Badge key={idx} variant="outline" className="bg-slate-50">
                      {kw}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {recentArticles.length > 0 && (
        <section className="py-12 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              Articles récents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recentArticles.map((item) => (
                <Card key={item.id} className="overflow-hidden hover:shadow-lg transition group">
                  <div className="h-40 bg-gradient-to-br from-orange-400 to-orange-600 relative">
                    {item.featured_image ? (
                      <img
                        src={item.featured_image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FileText className="w-12 h-12 text-white/50" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-slate-800 mb-2 line-clamp-2 group-hover:text-orange-600 transition">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {item.excerpt || item.meta_description}
                    </p>
                    <Link to={`/portail/blog/${item.slug}`}>
                      <Button variant="link" size="sm" className="p-0 text-orange-600">
                        Lire l'article →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Besoin d'une mini-pelle à La Réunion ?
          </h2>
          <p className="text-white/90 mb-6">
            IDMAT OI, distributeur officiel Liugong, vous accompagne dans le choix de votre engin.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/portail/machines">
              <Button className="bg-white text-orange-600 hover:bg-orange-50">
                Voir nos machines
              </Button>
            </Link>
            <Link to="/portail/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicBlogArticlePage;
